import axios from "axios";
import { useAuth } from "../store/useAuth";
import { AuthService } from "../services/authService";
import { LOG } from "../utils/logUtil";

axios.defaults.baseURL = process.env.REACT_APP_BASE_URL;
axios.defaults.headers.common["Content-Type"] = "application/json";
axios.defaults.headers.common.appVersion = "1.2.6";
axios.defaults.headers.common.os = "web";

const ongoingRequests = new Map();

axios.interceptors.request.use(async (config) => {
  const headerToken = config?.headers?.Authorization;

  // Create a unique key for the request
  const requestKey = `${config.method}:${config.url}:${JSON.stringify(
    config.params || {}
  )}:${JSON.stringify(config.data || {})}`;

  if (ongoingRequests.has(requestKey)) {
    // If the request is already in progress, cancel the new one
    return Promise.reject({ message: "Duplicate request", config });
  }

  // Add the request to the ongoing map
  ongoingRequests.set(requestKey, true);
  // Kept on the request: by the time the answer comes, config.data is the body
  // as sent (a JSON string), so a key built from it again never matched, the
  // entry stayed, and the next identical POST or PUT was dropped as a duplicate
  // while the caller saw success.
  (config as any).requestKey = requestKey;

  config.cancelToken = new axios.CancelToken((cancel) => {
    return cancel;
  });

  if (!headerToken) {
    /** If there is no headerToken, we try to get it from the storage */
    const accessToken = useAuth.getState().accessToken;
    const refreshToken = useAuth.getState().refreshToken;
    const isTokenExpired = AuthService.isTokenExpired(accessToken as string);
    const isRefreshTokenExpired = AuthService.isTokenExpired(
      refreshToken as string
    );
    if (isTokenExpired && isRefreshTokenExpired) {
      useAuth.getState().logout();
    }
    if (isTokenExpired && !isRefreshTokenExpired) {
      const newAuthData = await AuthService.refreshToken(
        refreshToken as string
      );
      if (newAuthData?.accessToken) {
        config.headers.Authorization = `Bearer ${newAuthData?.accessToken}`;
        return config;
      }
    }
    config.headers.Authorization = `Bearer ${accessToken}`;
    return config;
  }

  /** If there is header token, we check if it is expired or not */
  const isHeaderTokenExpired = AuthService.isTokenExpired(
    headerToken as string
  );
  const refreshToken = useAuth.getState().refreshToken;
  const isRefreshTokenExpired = AuthService.isTokenExpired(
    refreshToken as string
  );
  if (isHeaderTokenExpired && isRefreshTokenExpired) {
    useAuth.getState().logout();
  }
  if (isHeaderTokenExpired && !isRefreshTokenExpired) {
    const newAuthData = await AuthService.refreshToken(refreshToken as string);
    if (newAuthData?.accessToken) {
      config.headers.Authorization = `Bearer ${newAuthData?.accessToken}`;
      return config;
    }
  }

  config.headers.Authorization = `${headerToken}`;
  return config;
});

axios.interceptors.response.use(
  (response) => {
    const responseName =
      response?.request?.responseURL?.replace(
        process.env.REACT_APP_BASE_URL,
        ""
      ) ?? "";
    if (response?.status === 200) {
      LOG.info(`Response-${responseName}:`, { data: response?.data });
    } else {
      LOG.error(`Response-${responseName}:`, { data: response?.data });
    }
    ongoingRequests.delete((response.config as any).requestKey);
    return response;
  },
  (error) => {
    // if (error?.response?.status === 401) {
    //   useAuth.getState().refreshAccessToken();
    // }
    // A dropped duplicate has no key of its own: the original is still running.
    if (error?.config?.requestKey) ongoingRequests.delete(error.config.requestKey);
    if (error?.message !== 'Duplicate request') {
      return Promise.reject(error?.response ?? error);
    }
  }
);
